import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChatSidebar } from "./ChatSidebar";
import { ChatArea } from "./ChatArea";
import { useContactsPaginated, type ChatFilters } from "@/hooks/useContactsPaginated";
import { useMessagesPaginated } from "@/hooks/useMessagesPaginated";
import { useAuth } from "@/contexts/AuthContext";
import LeadManagementPanel from "./LeadManagementPanel";
import { supabase } from "@/lib/supabase";
import type { Database } from "@/lib/supabase";
import { type ChatFilterValues, defaultChatFilters } from "./ChatFilters";
import { clampChatAttributionId } from "@/lib/chatContactScope";
import { isContactUnread } from "@/utils/contactUtils";
import { renderMessageVariables } from "@/lib/messageVariables";
import { applySenderPrefix } from "@/lib/messageSenderPrefix";
import { useAccountMessageSettings } from "@/hooks/useAccountMessageSettings";
import { useFluxoSender } from "@/hooks/useFluxoSender";
import type { AuthUser } from "@/utils/sessionStorage";

type AccountContact = Database['public']['Tables']['account_contacts']['Row']

const ChatInterface = () => {
  const { contactId } = useParams<{ contactId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showUserNameInMessages } = useAccountMessageSettings();
  const [chatFilters, setChatFilters] = useState<ChatFilterValues>(defaultChatFilters);
  const filters: ChatFilters = {
    attributionId: chatFilters.attributionId,
    statusChat: chatFilters.statusChat,
    tag: chatFilters.tag,
  };
  const { contacts, loading: contactsLoading, hasMore: hasMoreContacts, loadMore: loadMoreContacts, getContactById, refetch: refetchContacts, updateContactLocally } = useContactsPaginated(filters)

  useEffect(() => {
    setChatFilters((prev) => {
      const nextAttr = clampChatAttributionId(prev.attributionId, user as AuthUser | null);
      if (nextAttr === prev.attributionId) return prev;
      return { ...prev, attributionId: nextAttr };
    });
  }, [
    user,
    user?.view_all_contacts,
    user?.view_unassigned_contacts,
    user?.auth_user_id,
    user?.type,
  ]);

  const [selectedContact, setSelectedContact] = useState<AccountContact | null>(null)
  const [selectedContactId, setSelectedContactId] = useState<string | null>(null)
  const [message, setMessage] = useState<string>("")

  // Refs para prevenir race conditions
  const contactIdRef = useRef<string | null>(null)
  const isNavigatingRef = useRef(false)
  // Evita disparar UPDATE duplicado quando o realtime devolve o mesmo last_read_at
  const markingReadRef = useRef<Set<string>>(new Set())
  const {
    messages,
    loading: messagesLoading,
    hasMore,
    isLoadingMore,
    loadMoreMessages,
    sendMessage,
    sendAudio,
    sendImage,
    sendVideo,
    sendDocument,
    sending
  } = useMessagesPaginated(selectedContact)

  const handleContactUpdate = async () => {
    // Atualizar lista de contatos
    await refetchContacts()
    // O useEffect abaixo vai atualizar o selectedContact automaticamente quando contacts mudar
  }

  const bumpConversationActivity = useCallback((contactId: string) => {
    const now = new Date().toISOString()
    updateContactLocally(contactId, { last_message_at: now })
    setSelectedContact(prev =>
      prev && prev.id === contactId ? { ...prev, last_message_at: now } : prev
    )
  }, [updateContactLocally])

  const { sendFluxo, sendingFluxo } = useFluxoSender({
    selectedContact,
    sendMessage,
    sendImage,
    sendAudio,
    onContactUpdate: handleContactUpdate,
    onConversationActivity: bumpConversationActivity,
  })

  const handleContactUpdateOptimistic = useCallback((updates: Partial<AccountContact>) => {
    if (!selectedContact) return;

    console.log('⚡ [ChatInterface] Optimistic update for contact:', selectedContact.id);

    // Atualizar contato selecionado imediatamente
    setSelectedContact(prev => prev ? { ...prev, ...updates } : null);

    // Atualizar na lista de contatos
    updateContactLocally(selectedContact.id, updates);
  }, [selectedContact, updateContactLocally]);

  // Marca um contato como lido (atualiza last_read_at no banco + otimista)
  const markContactAsRead = useCallback(async (contact: AccountContact) => {
    if (!supabase || !user?.account_id) return;
    if (!isContactUnread(contact)) return;
    if (markingReadRef.current.has(contact.id)) return;

    markingReadRef.current.add(contact.id);

    const readAt = contact.last_message_at ?? new Date().toISOString();
    updateContactLocally(contact.id, { last_read_at: readAt });
    setSelectedContact(prev => (prev && prev.id === contact.id ? { ...prev, last_read_at: readAt } : prev));

    try {
      const { error } = await supabase
        .from('account_contacts')
        .update({ last_read_at: readAt })
        .eq('id', contact.id)
        .eq('account_id', user.account_id);

      if (error) {
        console.error('❌ [ChatInterface] Failed to mark contact as read:', error);
      }
    } catch (err) {
      console.error('❌ [ChatInterface] Exception marking contact as read:', err);
    } finally {
      markingReadRef.current.delete(contact.id);
    }
  }, [user?.account_id, updateContactLocally]);

  // Selecionar contato pela URL (contactId)
  useEffect(() => {
    if (!contactId || contactsLoading) return;

    // Armazenar contactId atual
    contactIdRef.current = contactId

    const loadContact = async () => {
      console.log('🔍 [ChatInterface] Looking for contact by ID:', contactId);

      // Verificação ANTES de buscar para evitar fetch desnecessário
      if (contactIdRef.current !== contactId) {
        console.log('⚠️ [ChatInterface] ContactId changed before fetch, aborting');
        return;
      }

      const contact = await getContactById(contactId);

      // Verificação crítica: Só aplicar se contactId ainda é o mesmo
      if (contactIdRef.current !== contactId) {
        console.log('⚠️ [ChatInterface] ContactId changed during load, ignoring result');
        return;
      }

      if (contact) {
        console.log('✅ [ChatInterface] Found contact:', contact.jid);
        setSelectedContact(contact);
        setSelectedContactId(contact.id);
        markContactAsRead(contact);
      } else {
        console.warn('⚠️ [ChatInterface] Contact not found for ID:', contactId);
        setSelectedContact(null);
        setSelectedContactId(null);
        navigate('/chat', { replace: true });
      }
    };

    loadContact();
  }, [contactId, contactsLoading, getContactById, navigate, markContactAsRead]);

  // Selecionar primeiro contato se não houver contactId na URL
  useEffect(() => {
    if (!contactId && contacts.length > 0 && !selectedContactId && !contactsLoading) {
      console.log('🎯 [ChatInterface] No contactId in URL, auto-selecting first contact')
      const firstContact = contacts[0];
      navigate(`/chat/${firstContact.id}`, { replace: true });
    }
  }, [contactId, selectedContactId, navigate, contacts, contactsLoading]);

  // Atualizar selectedContact quando a lista de contatos mudar
  useEffect(() => {
    if (selectedContactId && contacts.length > 0) {
      const updatedContact = contacts.find(c => c.id === selectedContactId)
      if (updatedContact) {
        // Se chegou nova mensagem enquanto o chat estava aberto, marcar como lido
        if (isContactUnread(updatedContact)) {
          markContactAsRead(updatedContact);
        }

        // Só atualizar se dados realmente mudaram
        setSelectedContact(prev => {
          if (!prev) return updatedContact;

          // Comparar campos relevantes
          const hasChanges =
            prev.name !== updatedContact.name ||
            prev.display_name !== updatedContact.display_name ||
            prev.push_name_last !== updatedContact.push_name_last ||
            prev.last_message_at !== updatedContact.last_message_at ||
            prev.last_read_at !== updatedContact.last_read_at ||
            prev.status_chat !== updatedContact.status_chat ||
            prev.attribution_id !== updatedContact.attribution_id ||
            prev.account_assistant_id !== updatedContact.account_assistant_id ||
            JSON.stringify(prev.tags) !== JSON.stringify(updatedContact.tags);

          if (hasChanges) {
            console.log('🔄 [ChatInterface] Updating selected contact data')
            return updatedContact;
          }

          return prev;
        });
      }
    }
  }, [contacts, selectedContactId, markContactAsRead])

  // Realtime direto no contato selecionado — cobre reabertura quando o filtro
  // atual remove o contato da lista (ex.: status_chat concluido → aberto).
  useEffect(() => {
    if (!selectedContactId || !user?.account_id || !supabase) return;

    const client = supabase;
    const channel = client
      .channel(`selected-contact-${selectedContactId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'account_contacts',
          filter: `id=eq.${selectedContactId}`,
        },
        (payload) => {
          const updated = payload.new as AccountContact;
          if (!updated?.id) return;

          setSelectedContact((prev) => {
            if (!prev || prev.id !== updated.id) return updated;
            return { ...prev, ...updated };
          });

          updateContactLocally(updated.id, updated);
        }
      )
      .subscribe();

    return () => {
      client.removeChannel(channel);
    };
  }, [selectedContactId, user?.account_id, updateContactLocally])

  const handleContactSelect = useCallback((contact: AccountContact) => {
    if (isNavigatingRef.current) {
      return;
    }

    isNavigatingRef.current = true;
    contactIdRef.current = contact.id;

    markContactAsRead(contact);
    navigate(`/chat/${contact.id}`);
    setMessage("");

    // Resetar flag após navegação - reduzido para 50ms
    setTimeout(() => {
      isNavigatingRef.current = false;
    }, 50);
  }, [navigate, markContactAsRead]);

  const handleSendMessage = async () => {
    if (!selectedContact || !message.trim()) return

    const rawText = message.trim()
    const renderedText = renderMessageVariables(rawText, selectedContact)
    const messageToSend = applySenderPrefix(renderedText, user?.name, showUserNameInMessages)
    setMessage("")

    const success = await sendMessage(selectedContact.jid, messageToSend)
    if (!success) {
      setMessage(rawText)
    }
  }

  const handleSendAudio = async (audioUrl: string) => {
    if (!selectedContact) return false

    const success = await sendAudio(selectedContact.jid, audioUrl)
    return success
  }

  const handleSendImage = async (imageBase64: string, caption?: string, fileName?: string) => {
    if (!selectedContact) return false

    const success = await sendImage(selectedContact.jid, imageBase64, caption, fileName)
    return success
  }

  const handleSendVideo = async (videoBase64: string, caption?: string, fileName?: string) => {
    if (!selectedContact) return false

    const success = await sendVideo(selectedContact.jid, videoBase64, caption, fileName)
    return success
  }

  const handleSendDocument = async (documentBase64: string, fileName: string, caption?: string) => {
    if (!selectedContact) return false

    const success = await sendDocument(selectedContact.jid, documentBase64, fileName, caption)
    return success
  }

  return (
    <div className="flex flex-col md:flex-row gap-4 w-full h-full">
      <ChatSidebar
        contacts={contacts}
        selectedContact={selectedContact}
        onContactSelect={handleContactSelect}
        loading={contactsLoading}
        hasMore={hasMoreContacts}
        onLoadMore={loadMoreContacts}
        filters={chatFilters}
        onFiltersChange={setChatFilters}
      />
      <ChatArea
        selectedContact={selectedContact}
        messages={messages}
        message={message}
        setMessage={setMessage}
        onSendMessage={handleSendMessage}
        onSendAudio={handleSendAudio}
        onSendImage={handleSendImage}
        onSendVideo={handleSendVideo}
        onSendDocument={handleSendDocument}
        onSendFluxo={sendFluxo}
        loading={messagesLoading}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={loadMoreMessages}
        sending={sending || sendingFluxo}
        instance={(user?.account as any)?.instance || undefined}
        apiKey={(user?.account as any)?.api_token || undefined}
        onContactUpdate={handleContactUpdate}
        onContactUpdateOptimistic={handleContactUpdateOptimistic}
      />
      <LeadManagementPanel selectedContact={selectedContact} onContactUpdate={handleContactUpdate} />
    </div>
  );
};

export default ChatInterface;
